import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogBody, DialogFooter } from '@blueprintjs/core';
import { toast } from 'sonner';
import { connectGoogleDrive, getGoogleAccessToken } from '../../features/docs/googleToken';
import { GoogleDriveError, XLSX_MIME_TYPE, createGoogleSheetCopy } from '../../features/docs/drive';
import useAuth from '../../utils/useAuth';
import { downloadOriginalUrl, originalQuery } from './api';
import { StoredOriginalPreview } from './SpreadsheetPreview';
import { fmtBytes } from './ui';

/** Starts a browser download of the stored original. The signed URL carries the file name. */
export function useDownloadOriginal(batchId: number) {
  return useMutation({
    mutationFn: () => downloadOriginalUrl(batchId),
    onSuccess: (r) => { window.location.href = r.url; },
    onError: (e) => toast.error((e as Error).message),
  });
}

export class DriveNotConnected extends Error {}

/**
 * Copies a file into the signed-in user's Drive as a new Google Sheet and
 * opens it. The tab is opened on click so the browser does not block it as
 * a popup, then pointed at the sheet once Drive has converted it.
 */
export function useOpenInSheets(makeFile: () => Promise<{ name: string; blob: Blob }>) {
  const { session } = useAuth();
  return useMutation({
    mutationFn: async (tab: Window | null) => {
      try {
        const token = await getGoogleAccessToken(session);
        if (!token) throw new DriveNotConnected();
        const { name, blob } = await makeFile();
        const sheet = await createGoogleSheetCopy(name, blob, token);
        if (!sheet.webViewLink) throw new Error('Google Drive did not return a link to the new sheet.');
        if (tab) tab.location.href = sheet.webViewLink;
        else window.open(sheet.webViewLink, '_blank', 'noopener');
      } catch (e) {
        tab?.close();
        if (e instanceof GoogleDriveError && e.code === 'auth') throw new DriveNotConnected();
        throw e;
      }
    },
    onError: (e) => { if (!(e instanceof DriveNotConnected)) toast.error((e as Error).message); },
  });
}

/** Shown after Open in Google Sheets found no Drive connection. */
export function ConnectDriveBanner({ children }: { children: React.ReactNode }) {
  return (
    <div className="li-banner li-banner--warn" role="alert">
      <div className="li-banner__text">{children}</div>
      <button type="button" className="li-btn li-btn--secondary" onClick={() => void connectGoogleDrive()}>Connect Google Drive</button>
    </div>
  );
}

/**
 * The uploaded file, wherever it is mentioned: name and size as a button
 * that opens the read-only preview, with the download one click away.
 */
export function FileChip({ batchId, name, bytes }: { batchId: number; name: string | null | undefined; bytes?: number | null }) {
  const [open, setOpen] = useState(false);
  const label = name || 'Uploaded file';
  return (
    <>
      <button type="button" className="li-filechip" onClick={() => setOpen(true)} title={`Preview or download ${label}`}>
        <svg className="li-filechip__icon" viewBox="0 0 16 16" aria-hidden>
          <path d="M4 1.5h5.2L12.5 4.8v9.7H4z" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
          <path d="M9 1.5V5h3.5M6 8h4.5M6 10.5h4.5" fill="none" stroke="currentColor" strokeWidth="1.2" />
        </svg>
        <span className="li-filechip__name">{label}</span>
        {bytes != null && <span className="li-filechip__size">{fmtBytes(bytes)}</span>}
      </button>
      {open && <FilePreviewDialog batchId={batchId} name={label} bytes={bytes} onClose={() => setOpen(false)} />}
    </>
  );
}

/** The stored original, read-only, with its download. */
export function FilePreviewDialog({ batchId, name, bytes, onClose }: {
  batchId: number; name: string; bytes?: number | null; onClose: () => void;
}) {
  const download = useDownloadOriginal(batchId);
  const queryClient = useQueryClient();
  const sheets = useOpenInSheets(async () => ({
    name,
    blob: new Blob([await queryClient.fetchQuery(originalQuery(batchId))], { type: /\.csv$/i.test(name) ? 'text/csv' : XLSX_MIME_TYPE }),
  }));
  const needsDrive = sheets.error instanceof DriveNotConnected;
  return (
    <Dialog isOpen onClose={onClose} title={name} className="li-filedialog">
      <DialogBody>
        {needsDrive && (
          <ConnectDriveBanner>
            Connect Google Drive to open this file in Google Sheets. A copy is saved to your own Drive; the stored original is not changed.
            After connecting, open the file again and choose Open in Google Sheets.
          </ConnectDriveBanner>
        )}
        <StoredOriginalPreview batchId={batchId} filename={name} height="56vh" />
        <p className="li-muted li-filedialog__note">The file exactly as it was uploaded. It is never changed.</p>
      </DialogBody>
      <DialogFooter actions={<>
        <button type="button" className="li-btn li-btn--quiet" onClick={onClose}>Close</button>
        <button type="button" className="li-btn li-btn--secondary" disabled={sheets.isPending}
          onClick={() => sheets.mutate(window.open('', '_blank'))}>
          {sheets.isPending ? 'Copying to Drive…' : 'Open in Google Sheets'}
        </button>
        <button type="button" className="li-btn li-btn--primary" disabled={download.isPending} onClick={() => download.mutate()}>
          {download.isPending ? 'Preparing…' : `Download${bytes != null ? ` (${fmtBytes(bytes)})` : ''}`}
        </button>
      </>} />
    </Dialog>
  );
}
